import type { DocMode } from '@blocksuite/affine-model';
import { HighlightSelection } from '@blocksuite/affine-shared/selection';
import { Bound, deserializeXYWH } from '@blocksuite/global/gfx';
import { WidgetComponent } from '@blocksuite/std';
import { GfxControllerIdentifier, type GfxModel } from '@blocksuite/std/gfx';
import { computed, signal } from '@preact/signals-core';
import { cssVarV2 } from '@streamlain/theme/v2';
import { css, html, nothing, unsafeCSS } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';

type Anchor = {
  ids: string[];
  mode: DocMode;
  highlight: boolean;
  activityRunId?: string;
  activityLabel?: string;
};

export const AFFINE_SCROLL_ANCHORING_WIDGET = 'affine-scroll-anchoring-widget';

export function isNavigableAnchorBound(
  bounds: Bound | null | undefined
): bounds is Bound {
  return (
    !!bounds &&
    Number.isFinite(bounds.w) &&
    Number.isFinite(bounds.h) &&
    bounds.w > 0 &&
    bounds.h > 0
  );
}

export function shouldRenderAnchorBounds(
  mode: DocMode,
  bounds: Bound | null | undefined
): boolean {
  return !!bounds && (mode === 'page' || isNavigableAnchorBound(bounds));
}

export type AnchorBoundCandidate = {
  bounds: Bound | null | undefined;
  id?: string;
};

export type DegenerateAnchorBound = {
  bounds: Bound;
  id?: string;
};

export function resolveNavigableAnchorBounds(
  candidates: AnchorBoundCandidate[]
): {
  bounds: Bound | null;
  resolvedElementIds: string[];
  skippedDegenerateBounds: DegenerateAnchorBound[];
} {
  let bounds: Bound | null = null;
  const resolvedElementIds: string[] = [];
  const skippedDegenerateBounds: DegenerateAnchorBound[] = [];

  for (const candidate of candidates) {
    const candidateBounds = candidate.bounds;
    if (!candidateBounds) continue;

    if (!isNavigableAnchorBound(candidateBounds)) {
      skippedDegenerateBounds.push({
        bounds: candidateBounds,
        ...(candidate.id ? { id: candidate.id } : {}),
      });
      continue;
    }

    bounds = bounds ? bounds.unite(candidateBounds) : candidateBounds.clone();
    if (candidate.id) {
      resolvedElementIds.push(candidate.id);
    }
  }

  return { bounds, resolvedElementIds, skippedDegenerateBounds };
}

export type EdgelessAnchorFitData = {
  centerX: number;
  centerY: number;
  zoom: number;
};

export type EdgelessAnchorHighlightPresentation = {
  bounds: Bound;
  rotate: number;
};

export function resolveEdgelessAnchorHighlightPresentation(
  elements: GfxModel[],
  cameraBounds: Bound
): EdgelessAnchorHighlightPresentation {
  const element = elements.length === 1 ? elements[0] : null;
  if (element?.xywh) {
    const rawBounds = Bound.fromXYWH(deserializeXYWH(element.xywh));
    if (isNavigableAnchorBound(rawBounds)) {
      return {
        bounds: rawBounds,
        rotate: element.rotate ?? 0,
      };
    }
  }

  return { bounds: cameraBounds, rotate: 0 };
}

export function applyEdgelessAnchorNavigation(
  viewport: {
    setViewport: (
      zoom: number,
      center: [number, number],
      smooth: boolean
    ) => void;
  },
  fit: EdgelessAnchorFitData,
  selectTarget?: () => void
) {
  // Selection can schedule render work of its own. Complete it before the
  // canonical atomic viewport transition so nothing in the navigation path
  // can take camera ownership after the target movement has started.
  selectTarget?.();
  viewport.setViewport(fit.zoom, [fit.centerX, fit.centerY], true);
}
export function getNavigableAnchorFitToScreenData(
  viewport: {
    getFitToScreenData: (
      bounds: Bound,
      padding: [number, number, number, number]
    ) => EdgelessAnchorFitData;
  },
  bounds: Bound | null | undefined
): EdgelessAnchorFitData | null {
  if (!isNavigableAnchorBound(bounds)) return null;
  return viewport.getFitToScreenData(bounds, [20, 20, 100, 20]);
}

export class AffineScrollAnchoringWidget extends WidgetComponent {
  static override styles = css`
    :host {
      pointer-events: none;
      position: absolute;
      left: 0px;
      top: 0px;
      transform-origin: left top;
      contain: size layout;
      z-index: 1;

      & .highlight {
        position: absolute;
        box-sizing: border-box;
        pointer-events: none;

        &.edgeless {
          border-width: 1.39px;
          border-style: solid;
          border-color: ${unsafeCSS(
            cssVarV2('layer/insideBorder/primaryBorder')
          )};
          box-shadow: var(--streamlain-active-shadow);
        }

        &.page {
          border-radius: 5px;
          background-color: var(--streamlain-hover-color);
        }
      }

      & .agent-activity-badge {
        position: absolute;
        right: -10px;
        top: -10px;
        height: 20px;
        min-width: 20px;
        box-sizing: border-box;
        border: 1px solid #f59e0b;
        border-radius: 10px;
        padding: 0 6px;
        background: #fffbeb;
        color: #92400e;
        cursor: pointer;
        font-family: var(--streamlain-font-family);
        font-size: 10px;
        font-weight: 700;
        line-height: 18px;
        pointer-events: auto;
      }
    }
  `;

  #listened = false;

  readonly #requestUpdateFn = () => this.requestUpdate();

  readonly #resizeObserver: ResizeObserver = new ResizeObserver(
    this.#requestUpdateFn
  );

  anchor$ = signal<Anchor | null>(null);

  anchorBounds$ = signal<Bound | null>(null);

  anchorHighlight$ = signal<EdgelessAnchorHighlightPresentation | null>(null);

  highlighted$ = computed(() => this.std.selection.find(HighlightSelection));

  #getBoundsInEdgeless() {
    const controller = this.std.get(GfxControllerIdentifier);
    const presentation = this.anchorHighlight$.peek();
    if (!presentation) return;

    const { bounds, rotate } = presentation;
    const { x, y, w, h } = bounds;
    const zoom = controller.viewport.zoom;
    const [vx, vy] = controller.viewport.toViewCoord(x, y);

    return {
      bounds: new Bound(vx, vy, w * zoom, h * zoom),
      rotate,
    };
  }

  #getBoundsInPage(id: string) {
    const blockComponent = this.std.view.getBlock(id);
    if (!blockComponent) return;

    const container = this.host;
    const containerRect = container.getBoundingClientRect();
    const { left, top, width, height } = blockComponent.getBoundingClientRect();

    const offsetX = containerRect.left - container.offsetLeft;
    const offsetY = containerRect.top - container.offsetTop;

    return new Bound(left - offsetX, top - offsetY, width, height);
  }

  #moveToAnchorInEdgeless(ids: string[]) {
    const controller = this.std.get(GfxControllerIdentifier);
    const surface = controller.surface;
    if (!surface) return;

    const viewport = controller.viewport;
    let bounds: Bound | null = null;
    const resolvedElementIds: string[] = [];
    const skippedDegenerateBounds: DegenerateAnchorBound[] = [];

    // Strategy 1: Direct GfxModel lookup (works for notes, shapes, surface elements)
    const directBoundCandidates: AnchorBoundCandidate[] = [];
    const directElements: GfxModel[] = [];
    for (const id of ids) {
      const gfxElement = controller.getElementById<GfxModel>(id);
      if (!gfxElement) continue;
      directElements.push(gfxElement);
      const elementBound =
        'elementBound' in gfxElement && gfxElement.elementBound instanceof Bound
          ? gfxElement.elementBound
          : gfxElement.xywh
            ? Bound.fromXYWH(deserializeXYWH(gfxElement.xywh))
            : null;
      directBoundCandidates.push({ bounds: elementBound, id });
    }
    const directBounds = resolveNavigableAnchorBounds(directBoundCandidates);
    bounds = directBounds.bounds;
    resolvedElementIds.push(...directBounds.resolvedElementIds);
    skippedDegenerateBounds.push(...directBounds.skippedDegenerateBounds);

    // Strategy 2: DOM block lookup (works for paragraphs, headings inside notes)
    // The block itself is not a GfxModel, but its parent note is.
    if (!bounds) {
      for (const id of ids) {
        const blockComponent = this.std.view.getBlock(id);
        if (!blockComponent) continue;
        // Find the parent note and use its position
        let parent = blockComponent.parentComponent;
        while (parent && parent.flavour !== 'affine:note') {
          parent = parent.parentComponent;
        }
        if (parent) {
          const parentRect = parent.getBoundingClientRect();
          const blockRect = blockComponent.getBoundingClientRect();
          // Use parent note width but block's Y position (scroll to the right row)
          const coord = viewport.toModelCoordFromClientCoord([
            parentRect.left,
            blockRect.top,
          ]);
          const blockBound = new Bound(
            coord[0],
            coord[1],
            parentRect.width / viewport.zoom,
            blockRect.height / viewport.zoom
          );
          const blockBounds = resolveNavigableAnchorBounds([
            { bounds: blockBound, id },
          ]);
          if (blockBounds.bounds) {
            bounds = bounds
              ? bounds.unite(blockBounds.bounds)
              : blockBounds.bounds;
          }
          skippedDegenerateBounds.push(...blockBounds.skippedDegenerateBounds);
        }
      }
    }

    // Strategy 3: Block not rendered yet — wait for it (state-based)
    if (!bounds || !isNavigableAnchorBound(bounds)) {
      if (skippedDegenerateBounds.length) {
        console.warn(
          '[AffineScrollAnchoringWidget] Cannot navigate to degenerate edgeless anchor bounds',
          {
            docId: this.std.store.id,
            ids,
            mode: 'edgeless',
            skippedDegenerateBounds: skippedDegenerateBounds.map(item => ({
              ...(item.id ? { id: item.id } : {}),
              bounds: {
                x: item.bounds.x,
                y: item.bounds.y,
                w: item.bounds.w,
                h: item.bounds.h,
              },
            })),
          }
        );
      }
      if (!this.#listened) return;
      const pending = new Set(ids);

      const blockUpdatedSubscription =
        this.std.store.slots.blockUpdated.subscribe(v => {
          if (v.type === 'add' && pending.has(v.id)) {
            blockUpdatedSubscription.unsubscribe();
            this.#moveToAnchorInEdgeless(ids);
          }
        });

      const elementAddedSubscription = surface.elementAdded.subscribe(v => {
        if (pending.has(v.id) && v.local === false) {
          elementAddedSubscription.unsubscribe();
          this.#moveToAnchorInEdgeless(ids);
        }
      });

      this.disposables.add(blockUpdatedSubscription);
      this.disposables.add(elementAddedSubscription);
      return;
    }

    const fit = getNavigableAnchorFitToScreenData(viewport, bounds);
    if (!fit) return;
    applyEdgelessAnchorNavigation(viewport, fit, () => {
      if (resolvedElementIds.length) {
        controller.selection.set({
          elements: resolvedElementIds,
          editing: false,
        });
      }
    });

    this.#listened = false;
    this.anchorBounds$.value = bounds;
    this.anchorHighlight$.value = resolveEdgelessAnchorHighlightPresentation(
      directElements,
      bounds
    );
  }

  #moveToAnchorInPage(id: string) {
    const blockComponent = this.std.view.getBlock(id);
    if (!blockComponent) {
      if (!this.#listened) return;

      // listen for document updates
      const subscription = this.std.store.slots.blockUpdated.subscribe(v => {
        if (v.type === 'add' && v.id === id) {
          subscription.unsubscribe();
          this.#moveToAnchorInPage(id);
        }
      });
      this.disposables.add(subscription);
      return;
    }

    // use `requestAnimationFrame` to better scroll to the target
    // because sometimes it is impossible to scroll to the target
    requestAnimationFrame(() => {
      blockComponent.scrollIntoView({
        behavior: 'instant',
        block: 'center',
      });
    });

    this.#listened = false;
    this.anchorBounds$.value = Bound.fromDOMRect(
      blockComponent.getBoundingClientRect()
    );
  }

  override connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this.host);
    this.handleEvent('wheel', this.#requestUpdateFn);
    this.disposables.addFromEvent(window, 'resize', this.#requestUpdateFn);

    // Clears highlight
    this.disposables.addFromEvent(this.host, 'pointerdown', () => {
      this.#listened = false;
      this.anchor$.value = null;
      this.anchorBounds$.value = null;
      this.anchorHighlight$.value = null;
    });

    // In edgeless
    const controler = this.std.get(GfxControllerIdentifier);
    this.disposables.add(
      controler.viewport.viewportUpdated.subscribe(this.#requestUpdateFn)
    );

    this.disposables.add(
      this.anchor$.subscribe(anchor => {
        if (!anchor) return;

        const { mode, ids } = anchor;
        const id = ids[0];
        if (!id) return;

        if (mode === 'page') {
          this.#moveToAnchorInPage(id);
          return;
        }

        this.#moveToAnchorInEdgeless(ids);
      })
    );

    this.disposables.add(
      this.highlighted$.subscribe(highlighted => {
        if (!highlighted) return;

        const {
          mode,
          blockIds,
          elementIds,
          highlight,
          activityRunId,
          activityLabel,
        } = highlighted;
        const ids =
          mode === 'page'
            ? blockIds
            : elementIds.length
              ? elementIds
              : blockIds;
        if (!ids.length) return;

        // Consumes highlight selection
        this.std.selection.clear(['highlight']);

        // sl-vt9e.3.1: Set #listened BEFORE anchor to avoid race condition.
        // anchor$ triggers subscription synchronously → #moveToAnchorInEdgeless
        // may hit Strategy 3 which checks #listened. Must be true at that point.
        this.#listened = true;
        this.anchorHighlight$.value = null;
        this.anchor$.value = {
          mode,
          ids,
          highlight,
          ...(activityRunId ? { activityRunId } : {}),
          ...(activityLabel ? { activityLabel } : {}),
        };
      })
    );
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  override render() {
    const anchor = this.anchor$.value;
    if (!anchor || !anchor.highlight) return nothing;

    const { mode, ids } = anchor;
    const id = ids[0];
    if (!id) return nothing;

    const edgelessPresentation =
      mode === 'edgeless' ? this.#getBoundsInEdgeless() : undefined;
    const bounds =
      mode === 'page'
        ? this.#getBoundsInPage(id)
        : edgelessPresentation?.bounds;
    if (!bounds || !shouldRenderAnchorBounds(mode, bounds)) return nothing;

    const classes = { highlight: true, [mode]: true };
    const style = {
      left: `${bounds.x}px`,
      top: `${bounds.y}px`,
      width: `${bounds.w}px`,
      height: `${bounds.h}px`,
      transform:
        mode === 'edgeless' && edgelessPresentation?.rotate
          ? `rotate(${edgelessPresentation.rotate}deg)`
          : undefined,
      transformOrigin: 'center',
    };
    const activityLabel = anchor.activityLabel || 'Agent activity';

    return html`<div class=${classMap(classes)} style=${styleMap(style)}>
      ${anchor.activityRunId
        ? html`<button
            type="button"
            class="agent-activity-badge"
            title=${activityLabel}
            @pointerdown=${(event: Event) => event.stopPropagation()}
            @click=${(event: Event) => {
              event.stopPropagation();
              window.dispatchEvent(
                new CustomEvent('streamlain-agent-activity-focus', {
                  detail: {
                    runId: anchor.activityRunId,
                    label: anchor.activityLabel,
                  },
                })
              );
            }}
          >
            AI
          </button>`
        : nothing}
    </div>`;
  }
}
